import React, { useState, useEffect, useRef } from 'react'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''
import { ensureSessionHeaders } from '../../lib/serverSession';
import {
  getQuickChatConversations,
  getQuickChatMessagesForUser,
  sendSupportMessageAsAdmin,
  markQuickChatRead,
  type QuickChatConversation,
  type SupportMessage
} from '../../lib/support'

interface QuickChatAdminProps {
  adminUser?: any
}

const QuickChatAdmin: React.FC<QuickChatAdminProps> = ({ adminUser }) => {
  const [conversations, setConversations] = useState<QuickChatConversation[]>([])
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null)
  const [messages, setMessages] = useState<SupportMessage[]>([])
  const [newMessage, setNewMessage] = useState('')
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [adminUserId, setAdminUserId] = useState<number | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const messagesEndRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    loadAdminUserId()
  }, [adminUser])

  useEffect(() => {
    if (adminUserId) {
      loadConversations()
      // Refresh every 30 seconds
      const interval = setInterval(loadConversations, 30000)
      return () => clearInterval(interval)
    }
  }, [adminUserId])

  useEffect(() => {
    if (selectedUserId && adminUserId) {
      loadMessages(selectedUserId)
      // Mark messages from this user as read
      markQuickChatRead(adminUserId, selectedUserId).then(() => {
        // Refresh conversations to update unread counts
        loadConversations()
      })
    }
  }, [selectedUserId, adminUserId])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const loadAdminUserId = async () => {
    try {
      // Priority 1: Find admin user by their actual email (most reliable)
      if (adminUser?.email) {
        const response = await fetch(`${API_BASE_URL}/api/admin/generic`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(await ensureSessionHeaders()) },
          body: JSON.stringify({
            operation: 'select',
            data: { table: 'users', select: 'id', filters: { email: adminUser.email.toLowerCase() }, limit: 1 }
          })
        })
        const result = await response.json()
        if (result.data?.rows?.[0]?.id) {
          setAdminUserId(result.data.rows[0].id)
          return
        }
      }

      // Fallback: Get AUREUS system user
      const response2 = await fetch(`${API_BASE_URL}/api/admin/generic`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await ensureSessionHeaders()) },
        body: JSON.stringify({
          operation: 'select',
          data: { table: 'users', select: 'id', filters: { username: { op: 'ilike', value: 'AUREUS' } }, limit: 1 }
        })
      })
      const result2 = await response2.json()
      if (result2.data?.rows?.[0]?.id) {
        setAdminUserId(result2.data.rows[0].id)
      }
    } catch (e) {
      console.error('Error loading admin user ID:', e)
    }
  }

  const loadConversations = async () => {
    if (!adminUserId) return
    setLoading(true)
    try {
      const convs = await getQuickChatConversations(adminUserId)
      setConversations(convs)
    } catch (e) {
      console.error('Error loading conversations:', e)
    } finally {
      setLoading(false)
    }
  }

  const loadMessages = async (userId: number) => {
    if (!adminUserId) return
    try {
      const msgs = await getQuickChatMessagesForUser(adminUserId, userId)
      setMessages(msgs)
    } catch (e) {
      console.error('Error loading messages:', e)
    }
  }

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedUserId || !adminUserId) return
    
    setSending(true)
    try {
      const result = await sendSupportMessageAsAdmin(adminUserId, selectedUserId, newMessage.trim())
      if (result.success) {
        setNewMessage('')
        await loadMessages(selectedUserId)
        await loadConversations()
      } else {
        alert('Failed to send message: ' + result.error)
      }
    } catch (e) {
      console.error('Error sending message:', e)
    } finally {
      setSending(false)
    }
  }

  const filteredConversations = conversations.filter(conv => {
    if (!searchQuery.trim()) return true
    const query = searchQuery.toLowerCase()
    return (
      conv.username?.toLowerCase().includes(query) ||
      conv.first_name?.toLowerCase().includes(query) ||
      conv.last_name?.toLowerCase().includes(query) ||
      conv.email?.toLowerCase().includes(query)
    )
  })

  const selectedConversation = conversations.find(c => c.user_id === selectedUserId)

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 h-[calc(100vh-250px)]">
      {/* Conversation List */}
      <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden flex flex-col">
        <div className="p-4 border-b border-gray-700">
          <h3 className="text-lg font-semibold text-white mb-3">Quick Chat Messages</h3>
          <input
            type="text"
            placeholder="Search users..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm"
          />
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-4 text-center text-white">Loading...</div>
          ) : filteredConversations.length === 0 ? (
            <div className="p-4 text-center text-white">No conversations found</div>
          ) : (
            filteredConversations.map(conv => (
              <div
                key={conv.user_id}
                onClick={() => setSelectedUserId(conv.user_id)}
                className={`p-4 border-b border-gray-700 cursor-pointer hover:bg-gray-700 transition-colors ${
                  selectedUserId === conv.user_id ? 'bg-gray-700' : ''
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-white">
                    {conv.first_name || conv.last_name 
                      ? `${conv.first_name || ''} ${conv.last_name || ''}`.trim()
                      : conv.username}
                  </span>
                  {conv.unread_count > 0 && (
                    <span className="bg-yellow-600 text-white text-sm px-2 py-0.5 rounded-full">
                      {conv.unread_count}
                    </span>
                  )}
                </div>
                <div className="text-sm text-gray-500 mb-1">@{conv.username}</div>
                <div className="text-sm text-white truncate">{conv.last_message}</div>
                <div className="text-sm text-gray-500 mt-1">
                  {new Date(conv.last_message_at).toLocaleString()}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Chat Area */}
      <div className="lg:col-span-2 bg-gray-800 rounded-lg border border-gray-700 overflow-hidden flex flex-col">
        {selectedUserId && selectedConversation ? (
          <>
            {/* Chat Header */}
            <div className="p-4 border-b border-gray-700 bg-gray-900">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-yellow-600 rounded-full flex items-center justify-center text-white font-medium">
                  {(selectedConversation.first_name?.[0] || selectedConversation.username[0] || '?').toUpperCase()}
                </div>
                <div>
                  <div className="font-medium text-white">
                    {selectedConversation.first_name || selectedConversation.last_name 
                      ? `${selectedConversation.first_name || ''} ${selectedConversation.last_name || ''}`.trim()
                      : selectedConversation.username}
                  </div>
                  <div className="text-sm text-white">
                    @{selectedConversation.username} • {selectedConversation.email}
                  </div>
                </div>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.map(msg => (
                <div
                  key={msg.id}
                  className={`max-w-[80%] ${
                    msg.sender_id === adminUserId ? 'ml-auto' : ''
                  }`}
                >
                  <div className={`rounded-lg px-4 py-2 ${
                    msg.sender_id === adminUserId 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-gray-700 text-gray-100'
                  }`}>
                    {msg.message}
                  </div>
                  <div className={`text-sm text-gray-500 mt-1 ${
                    msg.sender_id === adminUserId ? 'text-right' : ''
                  }`}>
                    {new Date(msg.created_at).toLocaleString()}
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Reply Input */}
            <div className="p-4 border-t border-gray-700">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
                  placeholder="Type your reply..."
                  className="flex-1 px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                  disabled={sending}
                />
                <button
                  onClick={handleSendMessage}
                  disabled={sending || !newMessage.trim()}
                  className="px-6 py-2 bg-yellow-600 hover:bg-yellow-700 disabled:bg-gray-600 text-white rounded-lg font-medium transition-colors"
                >
                  {sending ? 'Sending...' : 'Send'}
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-white">
            <div className="text-center">
              <div className="text-4xl mb-4">💬</div>
              <p>Select a conversation to view messages</p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default QuickChatAdmin

